import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

import {
  Box,
  Divider,
  Link as FakeLink,
  TextField,
  Typography,
  styled,
  useTheme,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Switch,
  Collapse,
} from '@mui/material';

import { addLeadingZeros, getContrastYIQ } from '@frontend/features/common/utils/helpers';
import { ChipSelect } from '@frontend/features/common/components/ChipSelect';
import { caseStatusOptions } from '@frontend/features/cases/constants';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { useGetCaseById, useGetRelatedCases } from '@frontend/features/cases/queries';
import { useGetStepConnections, useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { useGetAllTeams } from '@frontend/features/teams/queries';
import { getTextColorFor } from '@frontend/features/common/helper';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { ArrowLeft, ArrowRight, Delete, Info, Work, ContentCopy } from '@mui/icons-material';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { WhiteHolder } from '@frontend/features/common/components/WhiteHolder';
import { LabelEdit } from '@frontend/features/common/components/LabelEdit';
import { CaseCustomFields } from '@frontend/features/cases/components/CaseCustomFields';
import { LinkToCaseTool } from '@frontend/features/sale-orders';
import { Link } from '@frontend/features/common/components/Link';
import { routes } from '@frontend/router/routes';
import { CaseTravelersSummary } from '@frontend/features/travelers/components';
import { CaseTravelSearchMenu } from './CaseTravelSearchMenu';

const DataBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '250px',
  width: '100%',
  gap: 1,
});

const StepActionIconButton = styled(IconButton)({
  width: 32,
  height: 32,
});

type ConnectionType = 'back' | 'forward';

interface CaseSidebarProps {
  // Mobile mode
  isMobile?: boolean;

  // Event handlers
  onEditCaseName: () => void;
  onCopyCaseNumber: () => void;
  onChangeStatus: (value: string | number) => void;
  onChangeWorkflow: (value: string | number) => void;
  onChangeIAEnabled: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeStep: (value: string | number) => void;
  onStepInstructionsOpen: () => void;
  onClientSelectorOpen: () => void;
  onOrganizationEditOpen: () => void;
  onAgencyEditOpen: () => void;
  onTeamSelectorOpen: () => void;
  onAgentSelectorOpen: () => void;
  onAssignToMe: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  onSaveLegacySaleOrder: (value: string) => void;
  onSaveLegacyReference: (value: string) => void;
  onRemoveRelation: (caseId: number, relationId: number) => void;
  onLinkCaseOpen: () => void;
}

export const CaseSidebar: React.FC<CaseSidebarProps> = ({
  isMobile = false,
  onEditCaseName,
  onCopyCaseNumber,
  onChangeStatus,
  onChangeWorkflow,
  onChangeIAEnabled,
  onChangeStep,
  onStepInstructionsOpen,
  onClientSelectorOpen,
  onOrganizationEditOpen,
  onAgencyEditOpen,
  onTeamSelectorOpen,
  onAgentSelectorOpen,
  onAssignToMe,
  onSaveLegacySaleOrder,
  onSaveLegacyReference,
  onRemoveRelation,
  onLinkCaseOpen,
}) => {
  const theme = useTheme();

  // No-op function for mobile mode
  const noOp = () => { };
  const { t } = useTranslation();
  const { id } = useParams();
  const { ability } = useAuth();

  // Data hooks
  const { data: caseData } = useGetCaseById({ id: Number(id) });
  const { data: relatedCases } = useGetRelatedCases({ id: Number(id) });
  const { data: stepConnections } = useGetStepConnections(caseData?.stepId);

  const { data: teams } = useGetAllTeams({
    pageSize: 10000,
    currentPage: 1,
    search: '',
  });

  const { data: workflows } = useGetWorkflowsByAgency({
    pageSize: 10000,
    currentPage: 1,
    search: '',
    agencyId: caseData?.agencyId ? String(caseData.agencyId) : undefined,
  });

  // Computed values
  const workflowOptions = workflows?.items?.map((wf) => ({
    value: wf.id,
    name: wf.name,
    color: wf.color,
    textColor: getTextColorFor(wf.color),
  }));

  const selectedTeam = teams?.items?.find((team) => team.id === caseData?.teamId);
  const selectedWorkflow = workflows?.items.find((workflow) => workflow.id === caseData?.workflowId);
  const createdAt = dayjs(caseData?.createdAt).format('DD MMMM YYYY - HH:mm');
  const disablePrevStep = !stepConnections || stepConnections.backward.length === 0 || !caseData?.workflowId;
  const disableNextStep = !stepConnections || stepConnections.forward.length === 0 || !caseData?.workflowId;

  // Local state for editing
  const [legacySaleOrderEdit, setLegacySaleOrderEdit] = useState(false);
  const [legacySaleOrderValue, setLegacySaleOrderValue] = useState('');
  const [legacyReferenceEdit, setLegacyReferenceEdit] = useState(false);
  const [legacyReferenceValue, setLegacyReferenceValue] = useState('');
  const [stepMenuAnchor, setStepMenuAnchor] = useState<Element | null>(null);
  const [connectionType, setConnectionType] = useState<ConnectionType | null>(null);
  const [customFieldsExpanded, setCustomFieldsExpanded] = useState(false);
  const [attributionExpanded, setAttributionExpanded] = useState(false);

  // Step navigation handlers
  const handleStepClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (stepMenuAnchor !== event.currentTarget) {
      setConnectionType(event.currentTarget.id as ConnectionType);
      setStepMenuAnchor(event.currentTarget);
    }
  };

  const handleStepClose = () => {
    setStepMenuAnchor(null);
    setConnectionType(null);
  };

  const renderConnectionItems = () => {
    const connectionOptions = connectionType === 'back' ? stepConnections?.backward : stepConnections?.forward;

    return connectionOptions?.map((step) => (
      <MenuItem key={step.id} onClick={() => { handleStepClose(); onChangeStep(step.id); }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ width: '100%' }}>
          <Chip size="small" label={step.name} />
        </Box>
      </MenuItem>
    ));
  };

  // Legacy sale order handlers
  const toggleSaleOrderEdit = () => {
    if (!legacySaleOrderEdit) {
      setLegacySaleOrderValue(caseData?.legacySaleOrderId || '');
    }
    setLegacySaleOrderEdit((prev) => !prev);
  };

  const handleChangeLegacySaleOrder = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLegacySaleOrderValue(event.target.value);
  };

  const handleSaveLegacySaleOrder = () => {
    onSaveLegacySaleOrder(legacySaleOrderValue);
    setLegacySaleOrderEdit(false);
  };

  const handleCancelChangeLegacySaleOrder = () => {
    setLegacySaleOrderValue(caseData?.legacySaleOrderId || '');
    setLegacySaleOrderEdit(false);
  };

  // Legacy reference handlers
  const toggleReferenceEdit = () => {
    if (!legacyReferenceEdit) {
      setLegacyReferenceValue(caseData?.legacyReference || '');
    }
    setLegacyReferenceEdit((prev) => !prev);
  };

  const handleChangeLegacyReference = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLegacyReferenceValue(event.target.value);
  };

  const handleSaveLegacyReference = () => {
    onSaveLegacyReference(legacyReferenceValue);
    setLegacyReferenceEdit(false);
  };

  const handleCancelChangeLegacyReference = () => {
    setLegacyReferenceValue(caseData?.legacyReference || '');
    setLegacyReferenceEdit(false);
  };

  const toggleAttribution = () => {
    setAttributionExpanded((prev) => !prev);
  };

  const toggleCustomFields = () => {
    setCustomFieldsExpanded((prev) => !prev);
  };

  return (
    <OverlayScrollbarsComponent
      style={{
        ...(isMobile ? { width: '100%' } : { minWidth: 396, }),
        backgroundColor: theme.palette.paper.dark,
        boxSizing: 'border-box',
        height: '100%',
        ...(isMobile ? { paddingLeft: theme.spacing(2) } : {}),
        ...(isMobile ? {} : { boxShadow: theme.shadows[1] }),
      }}
      className="overlayscrollbars-react"
      options={{ scrollbars: { autoHide: 'scroll', theme: 'os-theme-light' } }}
      defer
    >
      <Box display="flex" flexDirection="column" alignItems="flex-start" alignSelf="stretch" px={2} pt={3} gap={3}>
        {/* Case Header */}
        <Box display="flex" gap={1} alignItems="center">
          <IconAvatar size="medium" backgroundColor={theme.palette.divider}>
            <Work htmlColor={theme.palette.common.white} />
          </IconAvatar>
          <DataBox>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              onClick={onEditCaseName}
              sx={{ cursor: 'pointer', maxWidth: 365 }}
            >
              <Tooltip title={caseData?.name}>
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant="subtitle2"
                  color="text.primary"
                >
                  {caseData?.name}
                </Typography>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {t('cases.view.case')} #: {caseData && addLeadingZeros(caseData?.id, defaultLeadingZeros)}
              </Typography>
              {caseData?.id && (
                <Tooltip title={t('cases.view.copyCaseNumber')}>
                  <IconButton
                    size="small"
                    onClick={onCopyCaseNumber}
                    sx={{
                      padding: 0.5,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ContentCopy sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </DataBox>
        </Box>

        <Box position="relative" width={`calc(100% - ${theme.spacing(1)})`}>
          <Divider aria-hidden="true" textAlign="left" sx={{ mb: 3 }} />
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Status Section */}
            <WhiteHolder title={t('cases.view.titleStatus')}>
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {t('cases.view.step')}
                    </Typography>
                    <StepActionIconButton disabled={isMobile} onClick={isMobile ? noOp : onStepInstructionsOpen}>
                      <Info htmlColor={theme.palette.info.main} />
                    </StepActionIconButton>
                  </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <StepActionIconButton disabled={disablePrevStep || isMobile} id="back" onClick={isMobile ? noOp : handleStepClick}>
                        <ArrowLeft />
                      </StepActionIconButton>

                      <Box sx={{ maxWidth: 200 }}>
                        <Tooltip title={caseData?.stepName || t('common.unassigned')}>
                          <Typography
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            color={caseData?.stepName ? 'text.primary' : 'text.disabled'}
                            variant="body2"
                          >
                            {caseData?.stepName || t('common.unassigned')}
                          </Typography>
                        </Tooltip>
                      </Box>

                      <StepActionIconButton disabled={disableNextStep || isMobile} id="forward" onClick={isMobile ? noOp : handleStepClick}>
                        <ArrowRight />
                      </StepActionIconButton>
                    </Box>
                  </Box>
                  {connectionType && (
                    <Menu
                      anchorEl={stepMenuAnchor}
                      open={Boolean(stepMenuAnchor)}
                      onClose={handleStepClose}
                      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    >
                      {renderConnectionItems()}
                    </Menu>
                  )}
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.status')}
                  </Typography>
                  {caseData && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <ChipSelect
                        onSelect={onChangeStatus}
                        options={caseStatusOptions(theme, t)}
                        value={t(`cases.status.${caseData?.status}`)}
                        backgroundColor={theme.palette.cases[caseData?.status as keyof typeof theme.palette.cases]?.bg}
                        textColor={theme.palette.cases[caseData?.status as keyof typeof theme.palette.cases]?.text}
                      />
                    </Box>
                  )}
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.workflow')}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ChipSelect
                      linkMode
                      onSelect={onChangeWorkflow}
                      backgroundColor={selectedWorkflow?.color || ''}
                      options={workflowOptions || []}
                      textColor={selectedWorkflow ? getContrastYIQ(theme, selectedWorkflow?.color) : 'black'}
                      value={selectedWorkflow?.name}
                    />
                  </Box>
                </Box>


                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.list.table.channel')}
                  </Typography>
                  <Chip
                    sx={{
                      backgroundColor: theme.palette.grey[900],
                      color: theme.palette.common.white,
                    }}
                    label={
                      caseData?.originatedByChannel
                        ? t(`cases.originatedByChannel.${caseData.originatedByChannel}`)
                        : t('common.unassigned')
                    }
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.iaEnabled')}
                  </Typography>
                  <Switch
                    checked={caseData?.iaEnabled ?? true}
                    onChange={onChangeIAEnabled}
                    size="small"
                    color="primary"
                  />
                </Box>
              </>
            </WhiteHolder>

            {/* Client Section */}
            <WhiteHolder title={t('cases.view.titleClient')}>
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.client')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={onClientSelectorOpen}>
                    {caseData?.clientId ? (
                      <Link
                        to={caseData?.clientId ? routes.dashboard.clientView.replace(':id', caseData?.clientId) : '#'}
                        underline="none"
                        href="#"
                      >
                        <Tooltip title={`${caseData?.clientFirstName} ${caseData?.clientLastName}`}>
                          <Typography variant="body2" color={'text.primary'}>
                            {caseData.clientFirstName} {caseData.clientLastName}
                          </Typography>
                        </Tooltip>
                      </Link>
                    ) : (
                      <Button size="small" onClick={onClientSelectorOpen} disabled={isMobile} color="inherit" variant="text">
                        <Typography textTransform="initial" variant="body2" color="text.disabled">
                          {t('common.unassigned')}
                        </Typography>
                      </Button>
                    )}
                  </LabelEdit>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.organization')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={onOrganizationEditOpen}>
                    {caseData?.organizationId ? (
                      <Tooltip title={caseData?.organizationName}>
                        <Typography variant="body2" color={'text.primary'}>
                          {caseData.organizationName}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Button size="small" onClick={onOrganizationEditOpen} disabled={isMobile} color="inherit" variant="text">
                        <Typography textTransform="initial" variant="body2" color="text.disabled">
                          {t('common.unassigned')}
                        </Typography>
                      </Button>
                    )}
                  </LabelEdit>
                </Box>
              </>
            </WhiteHolder>

            {caseData?.id && (
              <>
                <CaseTravelSearchMenu caseData={caseData} />
                {ability?.can('view', 'TravelServices') && (
                  <Link
                    underline="none"
                    to={`${routes.dashboard.travelBookings}?caseId=${caseData.id}`}
                    width="100%"
                  >
                    <Button variant="outlined" size="small" fullWidth>
                      {t('cases.view.travelBookings')}
                    </Button>
                  </Link>
                )}
                <CaseTravelersSummary
                  caseId={caseData.id}
                  agencyId={caseData.agencyId}
                  hasClient={Boolean(caseData.clientId)}
                />
              </>
            )}

            {/* Assignments Section */}
            <WhiteHolder title={t('cases.view.assignments')}>
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.agency')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} onClick={onAgencyEditOpen} disabled={isMobile || !ability?.can('manage', 'Agencies')}>
                    {caseData?.agencyName ? (
                      <Tooltip title={caseData?.agencyName}>
                        <Typography variant="body2" color={'text.primary'}>
                          {caseData.agencyName}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Button size="small" onClick={onAgencyEditOpen} color="inherit" variant="text">
                        <Typography textTransform="initial" variant="body2" color="text.disabled">
                          {t('common.unassigned')}
                        </Typography>
                      </Button>
                    )}
                  </LabelEdit>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.team')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={onTeamSelectorOpen}>
                    <Box display="flex" flexDirection="column">
                      {selectedTeam ? (
                        <Tooltip title={selectedTeam.name}>
                          <Typography variant="body2" color="text.primary">
                            {selectedTeam.name}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Button size="small" onClick={onTeamSelectorOpen} color="inherit" variant="text">
                          <Typography textTransform="initial" variant="body2" color="text.disabled">
                            {t('common.unassigned')}
                          </Typography>
                        </Button>
                      )}
                    </Box>
                  </LabelEdit>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.agent')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={onAgentSelectorOpen}>
                    <Box display="flex" flexDirection="column">
                      {caseData?.agentId ? (
                        ability?.can('manage', 'Users') ? (
                          <Link underline="none" to={routes.dashboard.userView.replace(':id', caseData.agentId)}>
                            <Tooltip title={`${caseData.agentFirstName} ${caseData.agentLastName}`}>
                              <Typography variant="body2" color="text.primary">
                                {caseData.agentFirstName} {caseData.agentLastName}
                              </Typography>
                            </Tooltip>
                          </Link>
                        ) : (
                          <Tooltip title={`${caseData.agentFirstName} ${caseData.agentLastName}`}>
                            <Typography variant="body2" color="text.primary">
                              {caseData.agentFirstName} {caseData.agentLastName}
                            </Typography>
                          </Tooltip>
                        )
                      ) : (
                        <Button size="small" onClick={onAgentSelectorOpen} color="inherit" variant="text">
                          <Typography textTransform="initial" variant="body2" color="text.disabled">
                            {t('common.unassigned')}
                          </Typography>
                        </Button>
                      )}
                    </Box>
                  </LabelEdit>
                </Box>
                <FakeLink textAlign="right" onClick={onAssignToMe} underline="none" sx={{ cursor: 'pointer' }}>
                  {t('cases.view.assignToMe')}
                </FakeLink>
              </>
            </WhiteHolder>

            {/* Other Details Section */}
            <WhiteHolder title={t('cases.view.titleOtherDetails')}>
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.saleorderLegacy')}
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={toggleSaleOrderEdit}>
                    <Box display="flex" maxWidth="200px" alignItems="center" gap={1} overflow="hidden">
                      {legacySaleOrderEdit ? (
                        <TextField
                          size="small"
                          onChange={handleChangeLegacySaleOrder}
                          value={legacySaleOrderValue}
                          onKeyDown={(e) => {
                            e.nativeEvent.key === 'Enter' && handleSaveLegacySaleOrder();
                            e.nativeEvent.key === 'Escape' && handleCancelChangeLegacySaleOrder();
                          }}
                          autoFocus
                        />
                      ) : (
                        <>
                          {caseData?.legacySaleOrderId ? (
                            <Link
                              target="_blank"
                              to={`https://power.mevuelo.com/salesorder/${caseData?.legacySaleOrderId}`}
                              underline="none"
                              href="#"
                            >
                              <Typography variant="body2" color="text.primary">
                                {caseData?.legacySaleOrderId}
                              </Typography>
                            </Link>
                          ) : (
                            <Button size="small" onClick={toggleSaleOrderEdit} disabled={isMobile} color="inherit" variant="text">
                              <Typography textTransform="initial" variant="body2" color="text.disabled">
                                {t('common.unassigned')}
                              </Typography>
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </LabelEdit>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    Legacy Reference
                  </Typography>
                  <LabelEdit isMobile={isMobile} disabled={isMobile} onClick={toggleReferenceEdit}>
                    <Box display="flex" maxWidth="200px" alignItems="center" gap={1} overflow="hidden">
                      {legacyReferenceEdit ? (
                        <TextField
                          size="small"
                          onChange={handleChangeLegacyReference}
                          value={legacyReferenceValue}
                          onKeyDown={(e) => {
                            e.nativeEvent.key === 'Enter' && handleSaveLegacyReference();
                            e.nativeEvent.key === 'Escape' && handleCancelChangeLegacyReference();
                          }}
                          autoFocus
                        />
                      ) : (
                        <>
                          {caseData?.legacyReference ? (
                            <Typography variant="body2" color="text.primary">
                              {caseData?.legacyReference || ''}
                            </Typography>
                          ) : (
                            <Button size="small" onClick={toggleReferenceEdit} color="inherit" variant="text">
                              <Typography textTransform="initial" variant="body2" color="text.disabled">
                                {t('common.unassigned')}
                              </Typography>
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </LabelEdit>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {t('cases.view.created')}
                  </Typography>
                  <Typography component="span" variant="body2" color="text.disabled">
                    {createdAt}
                  </Typography>
                </Box>
              </>
            </WhiteHolder>

            {/* Custom Fields */}
            {caseData && Boolean(caseData.workflowId) && (
              <>
                {isMobile ? (
                  <>
                    <FakeLink
                      textAlign="left"
                      onClick={toggleCustomFields}
                      underline="none"
                      color="text.primary"
                      sx={{ cursor: 'pointer' }}
                    >
                      {customFieldsExpanded ? t('cases.view.lessInfo') : t('cases.view.moreInfo')}
                    </FakeLink>
                    <Collapse in={customFieldsExpanded}>
                      <CaseCustomFields />
                    </Collapse>
                  </>
                ) : (
                  <CaseCustomFields />
                )}
              </>
            )}

            {/* Attribution */}
            {caseData &&
              Boolean(
                caseData.ctwaClid ||
                  caseData.adId ||
                  caseData.adSourceType ||
                  caseData.utmSource ||
                  caseData.utmCampaign ||
                  caseData.fbclid,
              ) && (
                <WhiteHolder title={t('cases.view.attribution.title')}>
                  <>
                    <FakeLink
                      textAlign="left"
                      onClick={toggleAttribution}
                      underline="none"
                      color="text.primary"
                      sx={{ cursor: 'pointer' }}
                    >
                      {attributionExpanded ? t('cases.view.lessInfo') : t('cases.view.moreInfo')}
                    </FakeLink>
                    <Collapse in={attributionExpanded}>
                      <Box display="flex" flexDirection="column" gap={1} mt={1}>
                        {[
                          { label: t('cases.view.attribution.utmSource'), value: caseData.utmSource },
                          { label: t('cases.view.attribution.utmCampaign'), value: caseData.utmCampaign },
                          { label: t('cases.view.attribution.adId'), value: caseData.adId },
                          { label: t('cases.view.attribution.adSourceType'), value: caseData.adSourceType },
                          { label: t('cases.view.attribution.ctwaClid'), value: caseData.ctwaClid },
                          { label: t('cases.view.attribution.fbclid'), value: caseData.fbclid },
                        ]
                          .filter((row) => Boolean(row.value))
                          .map((row) => (
                            <Box
                              key={row.label}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={1}
                            >
                              <Typography component="span" variant="body2" color="text.secondary">
                                {row.label}
                              </Typography>
                              <Tooltip title={row.value || ''}>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  color="text.disabled"
                                  sx={{
                                    maxWidth: 180,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {row.value}
                                </Typography>
                              </Tooltip>
                            </Box>
                          ))}
                      </Box>
                    </Collapse>
                  </>
                </WhiteHolder>
              )}
          </Box>
        </Box>

        {/* Related Cases */}
        <Box width="100%" display="flex" flexDirection="column" gap={2}>
          {relatedCases?.map((rc) => (
            <Box key={rc.id} maxWidth={365} overflow="hidden">
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                <Link underline="none" to={routes.dashboard.caseView.replace(':id', String(rc.caseId))}>
                  <Typography variant="body2" color="text.primary">
                    {t('cases.view.case')} # {addLeadingZeros(rc.caseId, defaultLeadingZeros)}:
                  </Typography>
                </Link>
                <Box display="flex" gap={1} alignItems="center">
                  <Chip color={rc.type === 'linked' ? 'info' : 'warning'} label={rc.type} />
                  <IconButton onClick={() => onRemoveRelation(caseData?.id || 0, rc.id)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Box>

              <Link underline="none" to={routes.dashboard.caseView.replace(':id', String(rc.caseId))}>
                <Tooltip title={rc.name}>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="caption"
                    component="div"
                  >
                    {rc.name}
                  </Typography>
                </Tooltip>
              </Link>
            </Box>
          ))}
        </Box>
        {!isMobile && (
          <>
            <FakeLink onClick={onLinkCaseOpen} underline="none" sx={{ cursor: 'pointer' }}>
              {t('cases.view.addRelationShip')}
            </FakeLink>
            <LinkToCaseTool caseId={caseData?.id || 0} />
          </>
        )}
      </Box>
    </OverlayScrollbarsComponent>
  );
};
